import React, { useState, useEffect } from 'react';
import { Box, Typography, Radio, RadioGroup, FormControlLabel, Button, IconButton } from '@mui/material';
import Grid from "@mui/material/Grid";
import DropDown from '../../Components/Common/DropDown/DropDown';
import CustomButton from '../../Components/Button/Button';
import { useLocation, useNavigate } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import { V_GetAllFormLabels,SavePolicyandEmailSetting, V_GetAdminGroupsByUPN, V_GetAllEmailTemplates, V_GetAllEntitiesForUserAdmin, V_GetAllPolicies, V_GetPolicyandEmailSetting } from '../../Reducers/dcoi';
import { ArrowBackIos, DeleteForever } from '@mui/icons-material';
import CheckIcon from '@mui/icons-material/Check';
import "./VcoiNewPolicyAndEmailSettingHome.css";
import Spinner from '../../Components/Common/Spinner/Spinner';

function NewPolicyAndEmailSettingHome() {
  const dispatch = useDispatch();
  const { V_policiesListByUPN, inProgress, V_emailTemplatesList, V_policiesList, V_languageList, V_AllEntitiesForUserAdmin } = useSelector((state) => state.dcoi);
  const navigate = useNavigate();
  const location = useLocation();
  const settingData = location.state?.settingData || null;
  const [policyDropdown, setPolicyDropdown] = useState(V_policiesList || []);
  const [emailTempDropdown, setEmailTempDropdown] = useState(V_emailTemplatesList || []);
  const [entity, setEntity] = useState(settingData?.EntityId || "");
  const [entityAdmin, setEntityAdmin] = useState(settingData?.UserGroupId || "");
  const [policy, setPolicy] = useState(settingData?.PolicyId || "");
  const [emailTemp, setEmailTemp] = useState(settingData?.EmailTemplateId || "");
  const [language, setLanguage] = useState(settingData?.FormLabelId || "");
  const [mappings, setMappings] = useState([]);
  const [defaultMapping, setDefaultMapping] = useState(null);
  const [error, setError] = useState("");


  useEffect(() => {
    const fetchData = async () => {
      await Promise.all([
        dispatch(V_GetAdminGroupsByUPN()),
        dispatch(V_GetAllEntitiesForUserAdmin()),
        dispatch(V_GetAllFormLabels()),
        dispatch(V_GetAllPolicies()),
        dispatch(V_GetAllEmailTemplates()),
        dispatch(V_GetPolicyandEmailSetting()),
      ]);
    };
    fetchData();
  }, [dispatch]);

  useEffect(() => {
    setPolicyDropdown(V_policiesList?.filter(item => item.FormLabelId === language));
    setEmailTempDropdown(V_emailTemplatesList.filter(item => item.FormLabelId === language));
  }, [settingData, language]);

  useEffect(() => {
    if (V_policiesList?.length > 0) {
      setPolicyDropdown(V_policiesList.filter(item => item.FormLabelId === language));
    }
    if (V_emailTemplatesList?.length > 0) {
      setEmailTempDropdown(V_emailTemplatesList.filter(item => item.FormLabelId === language));
    }
  }, [V_policiesList, V_emailTemplatesList, language]);

  useEffect(() => {
    if (settingData?.Mappings?.length > 0) {
      const loadedMappings = settingData?.Mappings.map((map, index) => ({
        id: Date.now() + index,
        language: map.Language || "Unknown",
        policy: map.PolicyTitle || "Unknown",
        emailTemp: map.EmailTemplateTitle || "Unknown",
        languageId: map.FormLabelId,
        policyId: map.PolicyId,
        emailTemplateId: map.EmailTemplateId,
        isDefault: map.isDefaultMapping === 1,
      }));

      setMappings(loadedMappings); // Set initial mappings

      // Set default mapping
      const defaultMap = loadedMappings.find((map) => map.isDefault);
      if (defaultMap) {
        setDefaultMapping(defaultMap.id);
      }
    }
  }, [settingData]); // Dependencies ensure it runs only when settingData changes



  useEffect(() => {
    if (language) {
      setPolicy("");
      setEmailTemp("");
      setPolicyDropdown(V_policiesList.filter(item => item.FormLabelId === language) || []);
      setEmailTempDropdown(V_emailTemplatesList.filter(item => item.FormLabelId === language) || []);
    }
  }, [language, V_policiesList, V_emailTemplatesList]);


  const handleAddMapping = () => {
    if (!language || !policy || !emailTemp) {
      setError("All fields are required.");
      return;
    }

    const languageItem = V_languageList.find(item => item.id === language);
    const policyItem = V_policiesList.find(item => item.id === policy);
    const emailTempItem = V_emailTemplatesList.find(item => item.id === emailTemp);

    const languageName = languageItem?.Language || "Unknown";
    const policyName = policyItem?.Title || "Unknown";
    const emailTempName = emailTempItem?.Title || "Unknown";

    // Check for duplicate mapping
    const isDuplicate = mappings.some(
      map => map.languageId === language && map.policyId === policy && map.emailTemplateId === emailTemp
    );

    if (isDuplicate) {
      setError("This mapping already exists.");
      return;
    }

    setError("");

    // Determine if it's the first mapping
    const isFirstMapping = mappings.length === 0;

    // Create new mapping
    const newMapping = {
      id: Date.now(),  // Unique ID for React rendering
      language: languageName,
      policy: policyName,
      emailTemp: emailTempName,
      languageId: language,
      policyId: policy,
      emailTemplateId: emailTemp,
      isDefault: isFirstMapping  // First mapping is default
    };

    setMappings(prevMappings => [...prevMappings, newMapping]);  // Preserve existing mappings

    if (isFirstMapping) {
      setDefaultMapping(newMapping.id);
    }
  };

  const handleDeleteMapping = (id) => {
    setMappings((prevMappings) => {
      const updatedMappings = prevMappings.filter((map) => map.id !== id);

      // If the deleted mapping was the default, set another mapping as default
      if (defaultMapping === id && updatedMappings.length > 0) {
        setDefaultMapping(updatedMappings[0].id);
        updatedMappings[0].isDefault = true;
      } else if (updatedMappings.length === 0) {
        setDefaultMapping(null);
      }

      return updatedMappings;
    });
  };

  const handleDefaultChange = (id) => {
    setDefaultMapping(id);
    setMappings(prevMappings =>
      prevMappings.map(map => ({
        ...map,
        isDefault: map.id === id // Only the selected mapping is marked as default
      }))
    );
  };

  const isSaveDisabled = !entity || !entityAdmin || mappings.length === 0;

  const handleSubmit = () => {
    dispatch(SavePolicyandEmailSetting({ GroupId: entityAdmin, EntityId: entity, mappingIds: mappings })).then((res) => {
      if (res.payload.status === 200) {
        navigate(`/app-vcoi/vcoisitecontent/VcoiManagePolicyAndEmailSetting`)
      }
    })
  }


  return (
    <>
      <div className='headerNavigation'>
        <Typography sx={{ fontSize: "16px", fontWeight: "500" }}>{settingData ? "Edit Policy and Email settings" : "Add Policy and Email settings"}</Typography>
        <Box>
          <Typography
            sx={{ color: " #212129", display: "flex", alignItems: "center", cursor: "pointer", "&:hover": { textDecoration: "underline" } }}
            onClick={() => navigate(`/app-vcoi/vcoisitecontent/VcoiManagePolicyAndEmailSetting`)}
          ><ArrowBackIos sx={{ fontSize: "inherit" }} />Back to List</Typography>
        </Box>
      </div>
      <Box className='boxAlignment'>
        <Grid container spacing={2} alignItems="flex-start">
          <Grid item xs={12} sm={12} md={6}>
            <Typography sx={{ fontWeight: "bold" }}>Entity Admin Group <span className='required'>*</span></Typography>
            <DropDown disabled={settingData} placeholder="--Select Group--" value={entityAdmin} options={V_policiesListByUPN} onChange={setEntityAdmin} />
          </Grid>
          <Grid item xs={12} sm={12} md={6}>
            <Typography sx={{ fontWeight: "bold" }}>Entity<span className='required'>*</span></Typography>
            <DropDown disabled={settingData} placeholder="--Select Entity--" value={entity} options={V_AllEntitiesForUserAdmin} onChange={setEntity} />
          </Grid>
        </Grid>
        <Typography sx={{ fontWeight: "bold" }}>Mapping Settings<span className='required'>*</span></Typography>
        <Grid container spacing={2} alignItems="flex-start">
          <Grid item xs={12} sm={12} md={4}>
            <DropDown
              placeholder="--Select Language--"
              options={V_languageList.filter(lang => !mappings.some(map => map.languageId === lang.id))}
              value={language}
              onChange={setLanguage}
            />
          </Grid>
          <Grid item xs={12} sm={12} md={3}>
            <DropDown placeholder="--Select Policy--" options={policyDropdown} value={policy} onChange={setPolicy} />
          </Grid>
          <Grid item xs={12} sm={12} md={3}>
            <DropDown placeholder="--Select Email Template--" options={emailTempDropdown} value={emailTemp} onChange={setEmailTemp} />
          </Grid>
          <Grid item xs={12} sm={12} md={2}>
            <CustomButton label="+ Add" onClick={() => handleAddMapping(language, policy, emailTemp)} />
          </Grid>
          <Grid item xs={12}>
            {error && <Typography color="error" sx={{ marginBottom: "10px" }}>{error}</Typography>}
            <RadioGroup value={defaultMapping} onChange={(e) => handleDefaultChange(Number(e.target.value))}>
              <Box
                sx={{
                  maxWidth: "100%", // Ensures it doesn't overflow the parent container
                }}
              >
                {mappings.map((map) => (<Box sx={{ overflowX: "auto" }}>
                  <Grid
                    container
                    key={map.id}
                    alignItems="center"
                    sx={{
                      minWidth: "600px", // Ensures each row has enough width for scrolling
                      padding: "8px",
                      marginBottom: "8px",
                      background: "white",
                      border: "1px solid #CCCCCC",
                      borderRadius: "4px",
                      display: "flex",
                      flexWrap: "nowrap",
                    }}
                  >
                    {/* Radio Button with Default Label */}
                    <Grid item xs={4} sm={3} md={3} sx={{ display: "flex", alignItems: "center", minWidth: "150px" }}>
                      <FormControlLabel
                        value={map.id}
                        control={<Radio />}
                        checked={map.id === defaultMapping}
                      />
                      {map.isDefault && (
                        <span className="defaultSpan">
                          Default <CheckIcon sx={{ width: "16px", marginLeft: "4px" }} />
                        </span>
                      )}
                    </Grid>

                    {/* Other Columns */}
                    <Grid item xs={2} sm={2} md={2}>
                      <Typography variant="body2">{map.language}</Typography>
                    </Grid>
                    <Grid item xs={3} sm={3} md={3}>
                      <Typography variant="body2">{map.policy}</Typography>
                    </Grid>
                    <Grid item xs={2} sm={2} md={2}>
                      <Typography variant="body2">{map.emailTemp}</Typography>
                    </Grid>

                    {/* Delete Button */}
                    <Grid item xs={2} sm={2} md={2} sx={{ display: "flex", justifyContent: "center", minWidth: "80px" }}>
                      <IconButton onClick={() => handleDeleteMapping(map.id)}>
                        <DeleteForever />
                      </IconButton>
                    </Grid>
                  </Grid>
                </Box>))}
              </Box>
            </RadioGroup>
          </Grid>


        </Grid>
      </Box>
      <Grid container spacing={2} alignItems="center" justifyContent="center">
        <Grid item xs={12} sm={12} md={12} sx={{ margin: "20px", display: "flex", justifyContent: "center" }}>
          <Button sx={{
            backgroundColor: isSaveDisabled ? "#ccc" : " #212129",
            fontSize: "14px",
            fontWeight: "400",
            color: "white",
            borderRadius: "0px",
            "&:hover": {
              backgroundColor: isSaveDisabled ? "#ccc" : " #212129",
            },
          }} disabled={isSaveDisabled} onClick={handleSubmit}>{settingData ? "Update" : "Save"}</Button>
          &nbsp;&nbsp;
          <CustomButton onClick={() => navigate(`/app-vcoi/vcoisitecontent/VcoiManagePolicyAndEmailSetting`)} label="Cancel" />
        </Grid>
      </Grid>
      <Spinner isLoading={inProgress} />
    </>
  );
}

export default NewPolicyAndEmailSettingHome;
